/*
 * File: AddProductCard.tsx
 * Project: a-a
 * File Created: Monday, 23rd June 2025 9:12:40 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Thursday, 10th July 2025 6:34:43 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2025 Mutual Mobile, Mutual Mobile
 */
import React, { useState } from 'react';
import CustomCard from '../../../CustomCard/CustomCard';
import zeplinColor from '../../../../../theme/Color';
import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import CustomTextField from '../../../CustomTextField/CustomTextField';
import { useInput } from '../../../../../customHook/useInput';
import CustomDatePicker from '../../../CustomDatePicker/CustomDatePicker';
import dayjs, { Dayjs } from 'dayjs';

const AddProductCard = ({
  handleInputChange,
  value,
  data,
  index,
  loading,
  pricebookName,
}: {
  handleInputChange: (field: string, value: any, index: any) => void;
  value: any;
  data: any;
  index: number;
  loading: boolean;
  pricebookName: string;
}) => {
  const theme = useTheme();
  const {
    handleChange: handleAmountChange,
    handleBlur: handleAmountBlur,
    clearInput: clearAmount,
    value: amount,
    invalid: invalidAmount,
    didChange: didAmountChange,
  } = useInput(
    !loading && value?.products?.[index]?.UnitPrice
      ? value?.products?.[index]?.UnitPrice
      : value?.products?.[index]?.SalesPrice
      ? value?.products?.[index]?.SalesPrice
      : data?.UnitPrice
      ? data?.UnitPrice
      : '',
    () => true,
  );
  const {
    handleChange: handleQuantityChange,
    handleBlur: handleQuantityBlur,
    clearInput: clearQuantity,
    value: quantity,
    invalid: invalidQuantity,
    didChange: didQuantityChange,
  } = useInput(
    !loading && value?.products?.[index]?.Quantity
      ? value?.products?.[index]?.Quantity
      : data?.Quantity
      ? data?.Quantity
      : '',
    () => true,
  );
  const {
    handleChange: handleDescriptionChange,
    handleBlur: handleDescriptionBlur,
    clearInput: clearDescription,
    value: description,
    invalid: invalidDescription,
    didChange: didDescriptionChange,
  } = useInput('', () => true);
  const [active, setActive] = useState(false);
  const [date, setDate] = useState<Dayjs | null>(
    !loading && value?.products?.[index]?.ServiceDate
      ? dayjs(value?.products?.[index]?.ServiceDate)
      : data?.ServiceDate
      ? dayjs(data?.ServiceDate)
      : null,
  );
  return (
    <CustomCard
      styles={{
        borderRadius: '14px',
        padding: '24px',
        backgroundColor: zeplinColor.Neutral1,
        border: `1px solid ${zeplinColor.Neutral5}`,
        // width: '312px',
        // height: '201px',
        boxShadow: 'rgba(0, 0, 0, 0.15)',
        '&:hover': { border: `1px solid ${zeplinColor.BasicBlack}` },
        // display: 'flex',
        // flexDirection: 'column',
        // justifyContent: 'space-between',
      }}
    >
      {' '}
      {active ? (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}
          onClick={() => setActive((prev) => false)}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '24px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
              <Typography variant='body2'>{data?.name || data?.ProductName || 'NA'}</Typography>
              <Box sx={{ width: '100%' }}>
                <Chip
                  label={<Typography variant='label1'>{pricebookName}</Typography>}
                  sx={{
                    bgcolor: zeplinColor.Neutral4,
                    // Constants.PriorityTask[(data?.priority || 'Medium') as 'Normal' | 'Low' | 'High'],
                    color: zeplinColor.BasicBlack,
                    // width: '100%',
                    // textAlign: 'left',
                    '&.MuiChip-root': {
                      height: '22px',
                      padding: '4px 8px',
                      borderRadius: '50px',

                      // margin: 0,
                      // flex: 1,
                    },
                    '& .MuiChip-label': {
                      padding: 0,
                      margin: 0,
                    },
                  }}
                />
              </Box>
            </Box>
            <Box onClick={(e) => e.stopPropagation()}>
              <CustomTextField
                style={{ width: '100%', flex: 1 }}
                onBlur={() => {
                  handleAmountBlur && handleAmountBlur();
                }}
                onChange={(e) => {
                  handleAmountChange && handleAmountChange(e);
                  handleInputChange('UnitPrice', e.target.value, index);
                }}
                name={'Sales price'}
                text={amount ? amount : ''}
                error={invalidAmount && didAmountChange ? 'Please enter valid Amount' : ''}
                label={'Sales price'}
                type='number'
                tabindex={1}
                // required
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: '24px' }}>
            <Box onClick={(e) => e.stopPropagation()}>
              <CustomTextField
                style={{ width: '100%' }}
                onBlur={() => {
                  handleQuantityBlur && handleQuantityBlur();
                }}
                onChange={(e) => {
                  handleQuantityChange && handleQuantityChange(e);
                  handleInputChange('Quantity', e.target.value, index);
                }}
                name={'Quantity'}
                text={quantity ? quantity : ''}
                error={invalidQuantity && didQuantityChange ? 'Please enter valid Quantity' : ''}
                label={'Quantity'}
                type='number'
                tabindex={1}
                required
              />
            </Box>
            <Box onClick={(e) => e.stopPropagation()}>
              <CustomDatePicker
                value={date}
                setValue={(date) => {
                  setDate(date); // Update local state
                  handleInputChange(
                    'ServiceDate',
                    dayjs(date as Dayjs).format('YYYY-MM-DD'),
                    index,
                  ); // Pass the selected date to the parent component
                }}
                label={'Close date'}
              />
            </Box>
          </Box>
          <Box onClick={(e) => e.stopPropagation()}>
            <CustomTextField
              style={{ width: '100%' }}
              onBlur={handleDescriptionBlur}
              onChange={(e) => {
                handleDescriptionChange && handleDescriptionChange(e);
                handleInputChange('description', e.target.value, index);
              }}
              // onChange={handleDescriptionChange}
              name='Description'
              text={description}
              error={
                invalidDescription && didDescriptionChange ? 'Please enter valid description' : ''
              }
              label={'Description'}
              type='text'
              tabindex={1}

              //   required
            />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          onClick={() => setActive((prev) => true)}
        >
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Typography variant='body2'>{data?.name || data?.ProductName || 'NA'}</Typography>
              <Chip
                label={<Typography variant='label1'>{pricebookName}</Typography>}
                sx={{
                  bgcolor: zeplinColor.Neutral4,
                  // Constants.PriorityTask[(data?.priority || 'Medium') as 'Normal' | 'Low' | 'High'],
                  color: zeplinColor.BasicBlack,
                  // width: '39px',
                  // textAlign: 'left',
                  '&.MuiChip-root': {
                    height: '22px',
                    padding: '4px 8px',
                    borderRadius: '50px',

                    // margin: 0,
                    // flex: 1,
                  },
                  '& .MuiChip-label': {
                    padding: 0,
                  },
                }}
              />
            </Box>
          )}
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100%',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            <Typography variant='body1'>${data?.UnitPrice || data?.SalesPrice}</Typography>
          )}
        </Box>
      )}
    </CustomCard>
  );
};

export default AddProductCard;
